import type { EnvironmentConfig } from '@core/config-models';
import { getPageTitle } from '@core/jss-routing';
import type { Store } from '@ngrx/store';
import { select } from '@ngrx/store';
import { filter, map, take } from 'rxjs/operators';

const REMOVE_IDS_REGEX = /\/\d+/g; // Match numeric segments, including any following slashes

/**
 * Generates a simplified page name by processing a URL's hostname and path.
 * - The hostname is stripped of its extension and all `.` characters are replaced with `:`.
 * - The path removes numeric segments (e.g., `/a/123/b` becomes `/a/b`) and replaces `/` with `:`.
 * - The processed hostname and path are concatenated to form the page name.
 *
 * @param {string | URL} url - The URL string or URL object to process.
 * @returns {string} A simplified page name based on the URL.
 *
 * @example
 * formatUrlAsPageName('https://example.com/a/123/b')
 * // Returns 'example:a:b'
 */
export function formatUrlAsPageName(url: string | URL): string {
  // Ensure `url` is a URL object
  const urlObj = typeof url === 'string' ? new URL(url) : url;

  // Process the hostname: remove extension and replace '.' with ':'
  const hostname = urlObj.hostname;
  const domainWithoutDots = hostname
    .slice(
      0,
      hostname.lastIndexOf('.') === -1 ? undefined : hostname.lastIndexOf('.')
    )
    .replace(/\./g, ':');

  // Process the path: remove numeric segments, replace '/' with ':'
  const path = urlObj.pathname.length === 1 ? '' : urlObj.pathname;
  const pathWithoutIds = path
    .replace(REMOVE_IDS_REGEX, '/') // Remove numeric segments
    .replace(/\/+/g, '/'); // Normalize multiple slashes to a single slash

  // Remove trailing slash if present after numeric segment removal
  const cleanedPath = pathWithoutIds.endsWith('/')
    ? pathWithoutIds.slice(0, -1)
    : pathWithoutIds;

  // Only replace '/' with ':' if path is not empty
  const finalPath =
    cleanedPath.length > 0 ? cleanedPath.replace(/\//g, ':') : '';

  // Combine domain and path for the page name
  return `${domainWithoutDots}${finalPath}`;
}

/**
 * @deprecated getSiteContext should not be directly used by consuming components. The datatracking directives should
 *  internally resolve the element placement. If you **REALLY** need this, use `analyticsUtils.getSiteContext` instead.
 * @param store$ instance of the NGRX store.
 * @param config Environment Configuration.
 * @returns placement object, alike `"essent|open consument|besparen met isolatie"`
 */
/* istanbul ignore next */
export function getSiteContext(store$: Store<any>, config: EnvironmentConfig) {
  return store$.pipe(
    select(getPageTitle),
    filter((pageTitle): pageTitle is string => pageTitle != null),
    map((pageTitle: string) => {
      return `${config.brand}|${config.type} ${config.segment}|${pageTitle}`.toLowerCase();
    }),
    take(1)
  );
}
